import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Plus, Trash2, X } from "lucide-react";
import { type QuizDetail } from "~/api/types";
import { updateQuiz } from "~/api/v2/requests";
import { QuizReason } from "~/molecules/QuizReason";
import { Button } from "~/atoms/button";
import { Card, CardContent } from "~/atoms/card";
import { Input } from "~/atoms/input";
import { Label } from "~/atoms/label";
import { RadioGroup, RadioGroupItem } from "~/atoms/radio-group";
import { Textarea } from "~/atoms/textarea";

interface EditableOption {
  text: string;
  isCorrect: boolean;
}

interface EditableQuestion {
  question: string;
  options: EditableOption[];
  /** Stored reason HTML; "" when there is none. Editable only by removing it. */
  reason: string;
  /** Plain text for adding a new reason when there is none yet. */
  reasonDraft: string;
}

/** Escape a plain-text reason into the same restricted HTML the taker renders. */
const plainTextToHtml = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const escape = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return trimmed
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escape(paragraph).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

interface Props {
  quiz: QuizDetail;
  onSaved: () => void;
}

const emptyQuestion = (): EditableQuestion => ({
  question: "",
  options: [
    { text: "", isCorrect: true },
    { text: "", isCorrect: false },
  ],
  reason: "",
  reasonDraft: "",
});

const isValid = (questions: EditableQuestion[]): boolean =>
  questions.length > 0 &&
  questions.every(
    (question) =>
      question.question.trim().length > 0 &&
      question.options.length >= 2 &&
      question.options.every((option) => option.text.trim().length > 0) &&
      question.options.filter((option) => option.isCorrect).length === 1,
  );

/** Edit a quiz: question text, answer options, the correct option, and add or remove questions. */
const QuizEditor = ({ quiz, onSaved }: Props) => {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState<EditableQuestion[]>(() =>
    quiz.questions.map((question) => ({
      question: question.question,
      options: question.options.map((option) => ({ ...option })),
      reason: question.reason ?? "",
      reasonDraft: "",
    })),
  );
  const [isSaving, setIsSaving] = useState(false);

  const updateQuestion = (
    index: number,
    update: (question: EditableQuestion) => EditableQuestion,
  ) =>
    setQuestions((prev) =>
      prev.map((question, i) => (i === index ? update(question) : question)),
    );

  const setQuestionText = (index: number, text: string) =>
    updateQuestion(index, (question) => ({ ...question, question: text }));

  const setReasonDraft = (index: number, reasonDraft: string) =>
    updateQuestion(index, (question) => ({ ...question, reasonDraft }));

  const removeReason = (index: number) =>
    updateQuestion(index, (question) => ({
      ...question,
      reason: "",
      reasonDraft: "",
    }));

  const setOptionText = (qi: number, oi: number, text: string) =>
    updateQuestion(qi, (question) => ({
      ...question,
      options: question.options.map((option, i) =>
        i === oi ? { ...option, text } : option,
      ),
    }));

  const setCorrect = (qi: number, oi: number) =>
    updateQuestion(qi, (question) => ({
      ...question,
      options: question.options.map((option, i) => ({
        ...option,
        isCorrect: i === oi,
      })),
    }));

  const addOption = (qi: number) =>
    updateQuestion(qi, (question) => ({
      ...question,
      options: [...question.options, { text: "", isCorrect: false }],
    }));

  const removeOption = (qi: number, oi: number) =>
    updateQuestion(qi, (question) => {
      const removed = question.options[oi];
      const options = question.options.filter((_, i) => i !== oi);
      // Keep exactly one correct option if the correct one was removed.
      if (removed?.isCorrect && options[0]) options[0].isCorrect = true;
      return { ...question, options };
    });

  const addQuestion = () => setQuestions((prev) => [...prev, emptyQuestion()]);

  const removeQuestion = (index: number) =>
    setQuestions((prev) => prev.filter((_, i) => i !== index));

  const save = async () => {
    setIsSaving(true);
    try {
      await updateQuiz(
        quiz.id,
        questions.map((question) => ({
          question: question.question.trim(),
          options: question.options.map((option) => ({
            text: option.text.trim(),
            isCorrect: option.isCorrect,
          })),
          reason:
            (question.reason.trim()
              ? question.reason
              : plainTextToHtml(question.reasonDraft)) || null,
        })),
      );
      onSaved();
      toast.success(t(($) => $.anchoring.quiz.edit.saved));
    } catch {
      toast.error(t(($) => $.anchoring.quiz.edit.error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {questions.map((question, qi) => (
        <Card key={qi}>
          <CardContent className="flex flex-col gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>
                  {t(($) => $.anchoring.quiz.edit.questionLabel, {
                    number: qi + 1,
                  })}
                </Label>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  disabled={questions.length <= 1}
                  onClick={() => removeQuestion(qi)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
              <Textarea
                value={question.question}
                onChange={(event) => setQuestionText(qi, event.target.value)}
                placeholder={t(
                  ($) => $.anchoring.quiz.edit.questionPlaceholder,
                )}
              />
            </div>

            <RadioGroup
              value={String(question.options.findIndex((o) => o.isCorrect))}
              onValueChange={(value) => setCorrect(qi, Number(value))}
            >
              {question.options.map((option, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <RadioGroupItem value={String(oi)} />
                  <Input
                    value={option.text}
                    onChange={(event) =>
                      setOptionText(qi, oi, event.target.value)
                    }
                    placeholder={t(
                      ($) => $.anchoring.quiz.edit.optionPlaceholder,
                    )}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    disabled={question.options.length <= 2}
                    onClick={() => removeOption(qi, oi)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
            </RadioGroup>

            <Button
              variant="ghost"
              size="sm"
              className="self-start"
              onClick={() => addOption(qi)}
            >
              <Plus className="size-4" />
              {t(($) => $.anchoring.quiz.edit.addOption)}
            </Button>

            <div className="space-y-2">
              <Label>{t(($) => $.anchoring.quiz.edit.reasonLabel)}</Label>
              {question.reason.trim() ? (
                <div className="bg-muted/40 flex flex-col gap-2 rounded-lg border p-3">
                  <QuizReason html={question.reason} />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive self-start"
                    onClick={() => removeReason(qi)}
                  >
                    <Trash2 className="size-4" />
                    {t(($) => $.anchoring.quiz.edit.removeReason)}
                  </Button>
                </div>
              ) : (
                <Textarea
                  value={question.reasonDraft}
                  onChange={(event) => setReasonDraft(qi, event.target.value)}
                  placeholder={t(
                    ($) => $.anchoring.quiz.edit.reasonPlaceholder,
                  )}
                />
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={addQuestion}>
          <Plus className="size-4" />
          {t(($) => $.anchoring.quiz.edit.addQuestion)}
        </Button>
        <Button
          onClick={() => void save()}
          disabled={!isValid(questions) || isSaving}
        >
          {t(($) => $.anchoring.quiz.edit.save)}
        </Button>
      </div>
    </div>
  );
};

export default QuizEditor;
